import Phaser from 'phaser'
import Swal from 'sweetalert2'
import Menu from '../objects/Menu'
import Face from '../objects/Face'
import Spot from '../objects/Spot'
import ColorUtils from '../tools/ColorUtils'
import Utils from '../tools/Utils'
import Ball from '../objects/Ball'
import SoundManager from '../managers/SoundManager'

const π = Math.PI
const margin = 20

let minSize
let menu

let origin
let spotRadius, spotStroke
let faceStroke = 4
let ballRadius
let R, r

let spots, faces, balls
let oldSpots

let graphics
let scale

let isShuffled = false

export default class DodecahedronScene extends Phaser.Scene {

    soundManager

	constructor() {
        super('Dodecahedron')

        this.soundManager = new SoundManager()
    }
    
    preload() {
        this.load.image('dice', 'images/shuffle.png')
        this.load.image('fullscreen', 'images/fullscreen.png')
        this.load.image('fullscreen_exit', 'images/fullscreen_exit.png')

        this.load.svg('volume_on', 'images/volume_on.svg')
        this.load.svg('volume_off', 'images/volume_off.svg')

        this.load.audio('a#1', ['sounds/asharp1.mp3'])
        this.load.audio('a#2', ['sounds/asharp2.mp3'])
        this.load.audio('c#1', ['sounds/csharp1.mp3'])
        this.load.audio('c#2', ['sounds/csharp2.mp3'])
        this.load.audio('c1', ['sounds/c1.mp3'])
        this.load.audio('c2', ['sounds/c2.mp3'])
        this.load.audio('d#1', ['sounds/dsharp1.mp3'])
        this.load.audio('d#2', ['sounds/dsharp2.mp3'])
        this.load.audio('f1', ['sounds/f1.mp3'])
        this.load.audio('f2', ['sounds/f2.mp3'])
        this.load.audio('g#1', ['sounds/gsharp1.mp3'])
        this.load.audio('g#2', ['sounds/gsharp2.mp3'])
    }

    create() {
        scale = this.scale
        this.createSounds()
        this.createScene()
        scale.on('resize', (gameSize) => {
            this.cameras.resize(gameSize.width, gameSize.height)
            this.destroy()
            this.createScene()
        })
    }

    createSounds() {
        this.soundManager.add('a#1', this.sound.add('a#1'))
        this.soundManager.add('a#2', this.sound.add('a#2'))
        this.soundManager.add('c#1', this.sound.add('c#1'))
        this.soundManager.add('c#2', this.sound.add('c#2'))
        this.soundManager.add('c1', this.sound.add('c1'))
        this.soundManager.add('c2', this.sound.add('c2'))
        this.soundManager.add('d#1', this.sound.add('d#1'))
        this.soundManager.add('d#2', this.sound.add('d#2'))
        this.soundManager.add('f1', this.sound.add('f1'))
        this.soundManager.add('f2', this.sound.add('f2'))
        this.soundManager.add('g#1', this.sound.add('g#1'))
        this.soundManager.add('g#2', this.sound.add('g#2'))
    }

    createScene() {
        this.setSizes()

        this.createMenu()
        this.createObjects()

        graphics = this.add.graphics()
        this.draw()
    }

    setSizes() {
        minSize = Math.min(this.game.scale.width, this.game.scale.height)

        spotRadius = (minSize - 2 * margin) / 13
        spotStroke = spotRadius / 20
        ballRadius = spotRadius / 2.2
        R = (minSize - 2 * margin - 2 * spotRadius) / 2
        r = R * 13 / 30
        origin = {
            x: this.game.scale.width / 2,
            y: this.game.scale.height / 2
        }
    }

    createMenu() {
        menu = new Menu(
            this.game.scale.width,
            this.game.scale.height,
            minSize,
            this.soundManager,
            this.shuffle,
            scale
        )
        for (let item of menu.items) {
            item.sprite = this.make.sprite({
                x: item.x,
                y: item.y,
                key: item.texture
            }, false)
            item.setSprite()
        }
    }

    createObjects() {
        this.createSpots()
        this.createFaces()
        this.createBalls()
    }

    createSpots() {
        if (spots) {
            oldSpots = spots.slice()
        }
        spots = []

        for (let i=0; i<10; i++) {
            spots.push(
                new Spot(
                    'dark' + i,
                    origin.x + R * Math.cos(2*i*π/10), 
                    origin.y - R *  Math.sin(2*i*π/10),
                    spotRadius,
                    ColorUtils.getColor(false, i),
                    spotStroke
                )
            )
        }

        for (let i=0; i<5; i++) {
            spots.push(
                new Spot(
                    'light' + i,
                    origin.x + r * Math.cos(2*i*π/5), 
                    origin.y - r *  Math.sin(2*i*π/5),
                    spotRadius,
                    ColorUtils.getColor(true, i),
                    spotStroke
                )
            )
        }
    }

    createFaces() {
        const lightPolygon = [spots[10], spots[11], spots[12], spots[13], spots[14]]
        const orangePolygon = [spots[1], spots[2], spots[11], spots[10], spots[0]]
        const yellowPolygon = [spots[3], spots[4], spots[12], spots[11], spots[2]]
        const greenPolygon = [spots[5], spots[6], spots[13], spots[12], spots[4]]
        const bluePolygon = [spots[7], spots[8], spots[14], spots[13], spots[6]]
        const purplePolygon = [spots[9], spots[0], spots[10], spots[14], spots[8]]

        faces = [
            new Face('lightFace', lightPolygon, faceStroke, 'a#1', 'a#2', this.soundManager),
            new Face('orangeFace', orangePolygon, faceStroke, 'c#1', 'c#2', this.soundManager),
            new Face('yellowFace', yellowPolygon, faceStroke, 'c1', 'c2', this.soundManager),
            new Face('greenFace', greenPolygon, faceStroke, 'd#1', 'd#2', this.soundManager),
            new Face('blueFace', bluePolygon, faceStroke, 'f1', 'f2', this.soundManager),
            new Face('purpleFace', purplePolygon, faceStroke, 'g#1', 'g#2', this.soundManager)
        ]

        const faceGraphics = this.make.graphics({ x: 0, y: 0, add: false })
        for (let face of faces) {
            faceGraphics.clear()
            face.draw(faceGraphics)
            face.sprite = this.make.sprite({
                x: face.origin.x,
                y: face.origin.y,
                origin: { x: 0, y: 0 },
                key: face.name
            }, false)
            face.createEvents(this.checkWin)
        }
        faceGraphics.destroy()        
    }

    createBalls() {
        balls = []

        for (let i=0; i<10; i++) {
            const ball = new Ball(
                'ball' + i,
                ballRadius,
                ColorUtils.getColor(false, i)
            )
            balls.push(ball)
            spots[i].ball = ball
        }

        for (let i=0; i<5; i++) {
            const ball = new Ball(
                'ball' + (10+i),
                ballRadius,
                ColorUtils.getColor(true, i)
            )
            balls.push(ball)
            spots[10+i].ball = ball
        }

        if (oldSpots) {
            for (let i=0; i<spots.length; i++) {
                spots[i].ball = balls.find(ball => ball.name == oldSpots[i].ball.name)
            }
        }

        const ballGraphics = this.make.graphics({ x: 0, y: 0, add: false })
        for (let ball of balls) {
            ballGraphics.clear()
            ball.draw(ballGraphics)
        }
        ballGraphics.destroy()
    }

    draw() {
        for (let item of menu.items) {
            this.add.existing(item.sprite)
        }
        for (let face of faces) {
            this.add.existing(face.sprite)
        }
        for (let spot of spots) {
            spot.draw(graphics)
            spot.ball.sprite = this.add.follower(null, spot.x, spot.y, spot.ball.name)
        }
    }

    async shuffle() {
        let faceIndices = []
        let sides = []
        const shuffleMovesCount = 14 + Utils.getRandomInt(8)
        for (let i=0; i<shuffleMovesCount; i++) {
            faceIndices.push(Utils.getRandomInt(faces.length))
            sides.push(Utils.getRandomInt(2))
        }

        let moves = []
        for (let index of faceIndices) {
            if (sides[index] == 0) {
                moves.push(faces[index].rotateLeft)
            } else {
                moves.push(faces[index].rotateRight)
            }
        }
        for (let i=0; i<moves.length; i++) {
            await moves[i](faces[faceIndices[i]])
        }
        isShuffled = true
    }

    checkWin() {
        if (!isShuffled) return

        let win = true
        for (let spot of spots) {
            if (spot.color != spot.ball.color) {
                win = false
                break
            }
        }
        if (win) {
            Swal.fire('Good job!', '', 'success')
                .then((result) => {
                    if (result.value) {
                        isShuffled = false
                    }
                })
        }
    }

    update() {
        
    }

    destroy() {
        for (let face of faces) {
            face.destroy()
            this.textures.remove(face.name);
        }
        for (let ball of balls) {
            ball.destroy()
            this.textures.remove(ball.name);
        }
        menu.destroy()
        graphics.clear()
        graphics.destroy()
    }
}
